import { processError, logError } from '../errors/errorHandler'
import axios from 'axios'

// Base URL da API (deve vir do .env)
const API_BASE_URL = process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000/api'

// Timeout para requisições (10 segundos)
const REQUEST_TIMEOUT = 10000

// Criar instância do axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: REQUEST_TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
})

/**
 * Buscar todos os ingressos disponíveis para um evento
 * @param {string} eventSlug - Slug do evento
 * @returns {Promise<Array>} Lista de ingressos disponíveis
 */
export const getAvailableTickets = async (eventSlug) => {
  try {
    const response = await api.get(`/tickets/available/${eventSlug}`)

    return response.data.data || []
  } catch (error) {
    const processedError = processError(error, {
      operation: 'getAvailableTickets',
      eventSlug
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Buscar detalhes de um ingresso específico
 * @param {number} ticketId - ID do ingresso
 * @returns {Promise<Object>} Detalhes do ingresso
 */
export const getTicketById = async (ticketId) => {
  try {
    const response = await api.get(`/tickets/${ticketId}`)

    return response.data.data || null
  } catch (error) {
    const processedError = processError(error, {
      operation: 'getTicketById',
      ticketId
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Verificar disponibilidade de um ingresso
 * @param {number} ticketId - ID do ingresso
 * @param {number} quantity - Quantidade desejada
 * @returns {Promise<Object>} Status de disponibilidade
 */
export const checkTicketAvailability = async (ticketId, quantity) => {
  try {
    const response = await api.post('/tickets/check-availability', {
      ticket_id: ticketId,
      quantity
    })

    return {
      available: response.data.available || false,
      remaining: response.data.remaining || 0,
      message: response.data.message || ''
    }
  } catch (error) {
    const processedError = processError(error, {
      operation: 'checkTicketAvailability',
      ticketId,
      quantity
    })
    logError(processedError)
    throw processedError
  }
}

/**
 * Calcular valor total do ingresso com quantidade
 * @param {Object} ticket - Objeto do ingresso
 * @param {number} quantity - Quantidade
 * @returns {number} Valor total
 */
export const calculateTicketTotal = (ticket, quantity) => {
  if (!ticket || !ticket.value) return 0
  return parseFloat(ticket.value) * parseInt(quantity)
}

/**
 * Validar se quantidade está dentro dos limites do ingresso
 * @param {Object} ticket - Objeto do ingresso
 * @param {number} quantity - Quantidade desejada
 * @returns {Object} Resultado da validação
 */
export const validateTicketQuantity = (ticket, quantity) => {
  if (!ticket) {
    return {
      valid: false,
      message: 'Ingresso não encontrado'
    }
  }

  const qty = parseInt(quantity)

  if (isNaN(qty) || qty < 1) {
    return {
      valid: false,
      message: 'Quantidade inválida'
    }
  }

  if (ticket.min_per_order && qty < parseInt(ticket.min_per_order)) {
    return {
      valid: false,
      message: `Quantidade mínima: ${ticket.min_per_order} ingresso(s)`
    }
  }

  if (ticket.max_per_order && qty > parseInt(ticket.max_per_order)) {
    return {
      valid: false,
      message: `Quantidade máxima: ${ticket.max_per_order} ingresso(s)`
    }
  }

  if (ticket.available_quantity && qty > parseInt(ticket.available_quantity)) {
    return {
      valid: false,
      message: `Apenas ${ticket.available_quantity} ingresso(s) disponível(is)`
    }
  }

  return {
    valid: true,
    message: ''
  }
}

/**
 * Verificar se ingresso está dentro do período de vendas
 * @param {Object} ticket - Objeto do ingresso
 * @returns {boolean} Se está disponível para venda
 */
export const isTicketAvailable = (ticket) => {
  if (!ticket) {
    console.log('❌ isTicketAvailable: ticket is null');
    return false;
  }

  const now = new Date()

  if (ticket.sales_start) {
    const salesStart = new Date(ticket.sales_start)
    if (now < salesStart) {
      console.log(`❌ Ticket "${ticket.name}" - vendas ainda não iniciaram`, {
        now: now.toISOString(),
        salesStart: salesStart.toISOString(),
        ticket
      });
      return false
    }
  }

  if (ticket.sales_end) {
    const salesEnd = new Date(ticket.sales_end)
    if (now > salesEnd) {
      console.log(`❌ Ticket "${ticket.name}" - vendas já encerraram`, {
        now: now.toISOString(),
        salesEnd: salesEnd.toISOString()
      });
      return false
    }
  }

  if (ticket.available_quantity !== null && ticket.available_quantity !== undefined) {
    if (parseInt(ticket.available_quantity) <= 0) {
      console.log(`❌ Ticket "${ticket.name}" - sem estoque disponível`);
      return false
    }
  }

  console.log(`✅ Ticket "${ticket.name}" - DISPONÍVEL`);
  return true
}

/**
 * Formatar período de vendas para exibição
 * @param {Object} ticket - Objeto do ingresso
 * @returns {string} Período formatado
 */
export const formatSalesPeriod = (ticket) => {
  if (!ticket) return ''

  const formatDate = (dateString) => {
    if (!dateString) return null
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const start = formatDate(ticket.sales_start)
  const end = formatDate(ticket.sales_end)

  if (start && end) {
    return `${start} até ${end}`
  } else if (start) {
    return `A partir de ${start}`
  } else if (end) {
    return `Até ${end}`
  }

  return 'Sempre disponível'
}
